import type { UseFetchOptions } from '#app'
import type { MaybeRefOrGetter } from 'vue'
import type { ApiResponse } from '~/types/api.type'
import type { Client } from '~/types/client.type'
import type { MappedOmit } from '~/types/common.type'
import type { InvoicePosition } from '~/types/invoice.type'
import type { Project } from '~/types/project.type'
import type { TimeEntryFilter } from '~/types/reportFilter'
import type { TimeEntry } from '~/types/timeEntry.type'
import type { User } from '~/types/user.type'
import { defu } from 'defu'

export interface ApiTimeEntryGetListParams {
  userId?: User['id']
  projectId?: Project['id']
  timeIntervalStartAt: Date
  timeIntervalEndAt: Date
  withPending?: boolean
  onlyPending?: boolean
  clientId?: Client['id']
  onlyUnbilled?: boolean
  onlyBillable?: boolean
  withUser?: boolean
  filters?: Array<MappedOmit<TimeEntryFilter, 'type'> & { value: number[] }>
}

export type ApiTimeEntryCreateParams = Pick<TimeEntry, 'userId' | 'startAt' | 'endAt' | 'taskId' | 'notes' | 'isBilled'>
export type ApiTimeEntryCreatePendingParams = Omit<ApiTimeEntryCreateParams, 'startAt' | 'endAt' | 'userId'>
export type ApiTimeEntryUpdateParams = Pick<Partial<TimeEntry>, 'startAt' | 'endAt' | 'taskId' | 'isBilled' | 'notes' | 'userId'>

type ApiTimeEntry = TimeEntry<'withTask' | 'withProject' | 'withUser'>
type ApiTimeEntryPending = TimeEntry<'withTask' | 'withProject' | 'withUser' | 'withTaskList'>

export function useApiTimeEntryGetList<T extends ApiTimeEntryGetListParams>(params: MaybeRefOrGetter<T> = {} as T, options: UseFetchOptions<ApiResponse<ApiTimeEntry[]>> = {}) {
  const notOverridableOptions: UseFetchOptions<ApiResponse<ApiTimeEntry[]>> = { method: 'GET', params, immediate: false, watch: false }
  return useApiFetch('/time-entries', defu(notOverridableOptions, options))
}

export interface ApiTimeEntryUnbilledGetListParams {
  timeIntervalStartAt: Date
  timeIntervalEndAt: Date
  filters?: Array<MappedOmit<TimeEntryFilter, 'type'> & { value: number[] }>
  ignoreIsBilledForInvoicePositionId?: InvoicePosition['id']
}

export interface ApiTimeEntryUnbilledGetListResponse {
  currentTimeEntries: TimeEntry[]
  olderTimeEntries: never
}

export function useApiTimeEntryUnbilledGetList<T extends ApiTimeEntryUnbilledGetListParams>(params: MaybeRefOrGetter<T>, options: UseFetchOptions<ApiResponse<ApiTimeEntryUnbilledGetListResponse>> = {}) {
  const notOverridableOptions: UseFetchOptions<ApiResponse<ApiTimeEntryUnbilledGetListResponse>> = { method: 'GET', params, immediate: false, watch: false }
  return useApiFetch('/time-entries/unbilled', defu(notOverridableOptions, options))
}

export function useApiTimeEntryGetById(id: MaybeRefOrGetter<TimeEntry['id']>, options: UseFetchOptions<ApiResponse<ApiTimeEntry>> = {}) {
  const notOverridableOptions: UseFetchOptions<ApiResponse<ApiTimeEntry>> = { method: 'GET' }
  return useApiFetch(() => `/time-entries/${toValue(id)}`, defu(notOverridableOptions, options))
}

export function useApiTimeEntryGetPending(options: UseFetchOptions<ApiResponse<ApiTimeEntry>> = {}) {
  const notOverridableOptions: UseFetchOptions<ApiResponse<ApiTimeEntry>> = { method: 'GET' }
  return useApiFetch('/time-entries/pending', defu(notOverridableOptions, options))
}

export function useApiTimeEntryCreate(timeEntry: MaybeRefOrGetter<ApiTimeEntryCreateParams>, options: UseFetchOptions<ApiResponse<ApiTimeEntry>> = {}) {
  const notOverridableOptions: UseFetchOptions<ApiResponse<ApiTimeEntry>> = { method: 'POST', body: timeEntry }
  const overridableOptions: UseFetchOptions<ApiResponse<ApiTimeEntry>> = { immediate: false, watch: false }
  return useApiFetch('/time-entries', defu(notOverridableOptions, options, overridableOptions))
}

export function useApiTimeEntryCreatePending(timeEntry: MaybeRefOrGetter<ApiTimeEntryCreatePendingParams>, options: UseFetchOptions<ApiResponse<ApiTimeEntryPending>> = {}) {
  const notOverridableOptions: UseFetchOptions<ApiResponse<ApiTimeEntryPending>> = { method: 'POST', body: timeEntry }
  const overridableOptions: UseFetchOptions<ApiResponse<ApiTimeEntryPending>> = { immediate: false, watch: false }
  return useApiFetch('/time-entries/pending/start', defu(notOverridableOptions, options, overridableOptions))
}

export function useApiTimeEntryUpdateById(id: MaybeRefOrGetter<TimeEntry['id']>, timeEntry: MaybeRefOrGetter<ApiTimeEntryUpdateParams> = {}, options: UseFetchOptions<ApiResponse<ApiTimeEntry>> = {}) {
  const notOverridableOptions: UseFetchOptions<ApiResponse<ApiTimeEntry>> = { method: 'PATCH', body: timeEntry }
  const overridableOptions: UseFetchOptions<ApiResponse<ApiTimeEntry>> = { immediate: false, watch: false }
  const url = computed(() => {
    return `/time-entries/${toValue(id)}`
  })
  return useApiFetch(url, defu(notOverridableOptions, options, overridableOptions))
}

export function useApiTimeEntryStopPending(options: UseFetchOptions<ApiResponse<ApiTimeEntryPending>> = {}) {
  const notOverridableOptions: UseFetchOptions<ApiResponse<ApiTimeEntryPending>> = { method: 'PUT' }
  const overridableOptions: UseFetchOptions<ApiResponse<ApiTimeEntryPending>> = { immediate: false, watch: false }

  return useApiFetch('/time-entries/pending/stop', defu(notOverridableOptions, options, overridableOptions))
}

export function useApiTimeEntryDeleteById(id: MaybeRefOrGetter<TimeEntry['id']>, options: UseFetchOptions<never> = {}) {
  const notOverridableOptions: UseFetchOptions<never> = { method: 'DELETE' }
  const overridableOptions: UseFetchOptions<never> = { immediate: false, watch: false }
  return useApiFetch(() => `/time-entries/${toValue(id)}`, defu(notOverridableOptions, options, overridableOptions))
}
